import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie';

export class Http2 {
    constructor(private readonly httpClient: HttpClient,
        private readonly cookies: CookieService,
        private readonly rootUrl?: string,
        private readonly pdauthAppName = 'MapCore',
        private readonly isApi = false) {
        if (rootUrl && rootUrl.lastIndexOf('/') !== rootUrl.length - 1) {
            rootUrl = rootUrl + '/';
        }
        this.rootUrl = rootUrl;
    }

    private readonly tQuery = 'query';
    private readonly tMutation = 'mutation';

    public get url() {
        return this.rootUrl;
    }

    public MaxQueryAndVariablesLength: number = 1000;

    /**
     * Perform any request type
     */
    public request<T>(url: string, config: IHttp2RequestConfig): Promise<T> {
        if (config.file) {
            const formData: FormData = new FormData();
            formData.append('file', config.file, config.file.name);
            config = {
                method: 'POST',
                body: formData,
                headers: {}
            };
        } else {
            const isFormData = config.body && config.body instanceof FormData;
            if (config.body && !isFormData) {
                config.body = JSON.stringify(config.body);
            }
            config = {
                headers: !isFormData ? {
                    'Content-Type': 'application/json'
                } : {},
                params: {},
                ...config
            };
        }

        url = this.rootUrl ? this.rootUrl + url : url;
        // remove any trailing slash from the url
        if (url.lastIndexOf('/') === url.length - 1) {
            url = url.substring(0, url.length - 1);
        }

        if (this.isApi && !url.startsWith(window.location.origin)) {
            const token = this.cookies.get('PDAuth_token');
            if (token) {
                config.headers['X-Auth-Token'] = token;
            }
            config.withCredentials = true;
        }

        if (config.params && config.params.variables) {
            config.params.variables = config.params.variables.replaceAll('+', '%2B'); // Urlencode plus sign otherwise the plus sign is replaced with a space.
        }

        return this.httpClient.request<T>(config.method, url, config).toPromise().then(result => {
            return result;
        }, errorResponse => {
            return Promise.reject(errorResponse.error);
        });
    }

    /**
     * Perform a GET graphql query
     * @param query GraphQL query
     * @param variables variables to be inserted into query
     * @param method "GET" | "POST" | "PUT" | "DELETE"
     * @param dataPath retrieves the data at the specified named path.
     */
    public async graphqlRequest<T = any>(query: string, variables: any, method: Method = 'GET', dataPath: string = null) {
        let queryName: string;
        const pdauthAppName = this.pdauthAppName;
        const type = query.trim().toLowerCase().startsWith(this.tQuery) ? this.tQuery : this.tMutation;
        if (method === 'GET' && (query.length + JSON.stringify(variables).length) > this.MaxQueryAndVariablesLength) {
            method = 'POST';
        }
        if (type === this.tQuery) {
            // find the query name from the query and use that in the querystring to help with debugging to distinguish this request
            queryName = query.substring(query.indexOf(this.tQuery) + this.tQuery.length, query.indexOf('{')).trim();
        } else {
            queryName = query.substring(query.indexOf(this.tMutation) + this.tMutation.length, query.indexOf('(')).trim();
        }
        // remove any excess whitespace from the query only for GET requests since this is passed on the URL
        if (method === 'GET') {
            query = this.graphqlTrim(query);
        }
        let response: { data: any };

        try {
            response = await this.request<{ data: any }>(`graphql?${queryName}&pdauthAppName=${pdauthAppName}`, {
                body: method !== 'GET' ? {
                    query,
                    variables
                } : undefined,
                method,
                params: method === 'GET' ? {
                    query,
                    variables: JSON.stringify(variables)
                } : undefined
            });
        } catch (e) {
            if (e && !e.message && e.errors && e.errors[0]) {
                e.message = e.errors[0].message;
            }
            throw e;
        }
        if (!dataPath) {
            return <T>response.data;
        } else {
            const parts = dataPath.split('.');
            let result = response.data;
            for (const part of parts) {
                result = result[part];
            }
            return <T>result;
        }
    }
    private graphqlTrim(text: string) {
        text = text.replace(/\s+/g, ' ');
        return text;
    }

    public get<T>(url: string, params?: any, config?: any): Promise<T> {
        const cfg = {
            ...config,
            method: 'GET',
            params
        };
        return this.request<T>(url, cfg);
    }

    public post<T>(url: string, body?: any, config?: any): Promise<T> {
        const cfg = {
            body,
            method: 'POST',
            ...config
        };
        return this.request<T>(url, cfg);
    }

    public put<T>(url: string, body?: any, config?: any): Promise<T> {
        const cfg = {
            body,
            method: 'PUT',
            ...config
        };
        return this.request<T>(url, cfg);
    }

    public delete<T>(url: string, body?: any, config?: any): Promise<T> {
        const cfg = {
            body,
            method: 'DELETE',
            ...config
        };
        return this.request<T>(url, cfg);
    }
}

@Injectable()
export class Http2Factory {
    constructor(private readonly httpClient: HttpClient, private readonly cookies: CookieService) { }
    public create(url: string, pdauthAppName: string = 'MapCore', isApi = false) {
        return new Http2(this.httpClient, this.cookies, url, pdauthAppName, isApi);
    }
}

export interface IHttp2RequestConfig {
    body?: any;
    file?: File;
    headers?: { [key: string]: any };
    method?: Method;
    params?: { [key: string]: any };
    withCredentials?: boolean;
}
export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';
